import { AfterViewInit, Component, Input } from '@angular/core';
import cytoscape, { CytoscapeOptions, StylesheetJsonBlock } from 'cytoscape';
import dagre, { DagreLayoutOptions } from 'cytoscape-dagre';
import { SvgDesignService } from '../../../../services/svg-design.service';
import { ToastrService } from 'ngx-toastr';
import { UtilitySharedService } from '../../../../services/shared/utility-shared.service';
import { SubstationTemplateService } from '../../../../services/substation-template.service';
import { NodeType } from '../../../../enums/node-type';

cytoscape.use(dagre);

const CY_STYLE: StylesheetJsonBlock[] = [
  {
    selector: 'node',
    style: {
      shape: 'rectangle',
      'border-width': 0,
      // label: 'data(label)',
      // 'text-valign': 'center',
      // 'text-halign': 'right',
      // color: '#000000',
      // 'text-margin-x': 5,
      // 'text-margin-y': 15
    },
  },
  {
    selector: 'edge',
    style: {
      width: 2,
      'line-color': '#000000',
      'curve-style': 'taxi',
      'taxi-direction': 'auto',
      'taxi-turn': 1000,
    },
  },
];

@Component({
  selector: 'lib-expanded-schematic-diagram',
  standalone: false,
  templateUrl: './expanded-schematic-diagram.component.html',
  styleUrls: ['./expanded-schematic-diagram.component.scss']
})

export class ExpandedSchematicDiagramComponent implements AfterViewInit {
  @Input() equipmentList: any[] = [];
  @Input() substationTemplateId: number | null = null;

  utilityId: number = 1;
  cy: cytoscape.Core | null = null;
  savedPositions: any;
  dbHasPositionData: boolean = false;

  constructor(private svgDesignService: SvgDesignService, 
    private toastr: ToastrService, 
    private ssTemplateService: SubstationTemplateService, 
    private utilitySharedService: UtilitySharedService)
  {
    this.utilityId = Number(this.utilitySharedService.getUtilityId());
  } 
  
  ngAfterViewInit(): void {
    this.loadNodePositionsFromDb();
  }

  refreshDiagram() {
    if (this.cy) {
      this.cy.destroy();
      this.cy = null;
    }
    this.generateSchematicDiagram();
  }

  generateSchematicDiagram(){
    const cyJson = this.createDiagramJson(this.dbHasPositionData);

    try
    {
      this.cy = cytoscape(cyJson);
    }
    catch
    {
      this.toastr.error("Something went wrong with generating diagram.")
      return;
    }

    // just in case of rare fail cases
    if (!this.cy){
      this.toastr.error("Something went wrong with generating diagram.")
      return;
    }

    this.createTooltips();
  }

  private createDiagramJson(dbHasPositionData: boolean): CytoscapeOptions {
    const equipments = this.equipmentList;
    const edges: any[] = [];
    const seenConnections = new Set<string>();

    for (const eq of equipments) {
      const fromGuid = eq.guid;

      if (!Array.isArray(eq.connectivities)) continue;

      for (const conn of eq.connectivities) {
        const toGuid = conn.guid;
        if (!toGuid || toGuid === fromGuid) continue;

        // Normalize connection key (order-independent)
        const connectionKey = [fromGuid, toGuid].sort().join('::');

        if (seenConnections.has(connectionKey)) {
          continue;
        }

        let source: string;
        let target: string;

        if (conn.nodeType === NodeType.Source || conn.nodeType === 'Source') {
          // Source → edge comes FROM the other node TO this one
          source = toGuid;
          target = fromGuid;
        } else if (conn.nodeType === NodeType.Destination || conn.nodeType === 'Destination') {
          // Destination → edge goes FROM this node TO the other
          source = fromGuid;
          target = toGuid;
        } else {
          continue;
        }

        seenConnections.add(connectionKey);

        edges.push({
          data: {
            id: connectionKey,
            source,
            target
          }
        });
      }
    }

    // saved positions (format: [{ id, position }])
    const positionMap = new Map<string, { x: number; y: number }>();
    if (this.dbHasPositionData && Array.isArray(this.savedPositions)) {
      for (const { id, position } of this.savedPositions) {
        if (id && position) {
          positionMap.set(id, position);
        }
      }
    }

    const nodes = equipments.map(eq => {
      const icon = this.svgDesignService.getSvgDesign(eq.visualizationConfiguration?.symbol, eq.visualizationConfiguration?.color);
      const position = this.dbHasPositionData ? positionMap.get(eq.guid) : undefined;
      
      return {
        data: { id: eq.guid, label: eq.className },
        ...(position ? { position } : {}),
        style: {
          width: icon.width,
          height: icon.height,
          'background-image': icon.svg
            ? `data:image/svg+xml;base64,${btoa(String.fromCharCode(...new TextEncoder().encode(icon.svg)))}`
            : '',
          'background-fit': 'contain',
          'background-opacity': 0,
        },
      };
    });

    return {
      container: document.getElementById('cy-expanded')!,
      elements: { nodes, edges },
      layout: dbHasPositionData
      ? { name: 'preset'}
      : {
        name: 'dagre',
        rankDir: 'TB',       // TB = Top → Bottom
        nodeSep: 80,         // horizontal spacing between nodes
        rankSep: 100,        // vertical spacing between layers
        edgeSep: 80,         // space between edges
        fit: true,
        nodeDimensionsIncludeLabels: true
        } as DagreLayoutOptions,
      style: CY_STYLE,
    };
  }

  publishSchematicDiagram(){
    const obj = {
      substationTemplateId: this.substationTemplateId,
      schematicLayoutJson: JSON.stringify(this.getMinimalCytoscapeNodes(this.cy!)),
      utilityId: this.utilityId
    }

    this.ssTemplateService.saveSchematicLayoutJson(obj).subscribe({
      next: (response: any) => {
        if (response?.body?.status == 200 || response?.body?.status == 'OK') {
          this.toastr.success('Schematic published successfully.');
        } else{
          this.toastr.error((response?.body?.message));
        }
      }
    })
  }

  getMinimalCytoscapeNodes(cy: cytoscape.Core) {
    return cy.nodes().map(node => {
      const json = node.json();
      return {
        id: json['data'].id,
        position: json['position']
      };
    });
  }

  loadNodePositionsFromDb() {
    const obj = {
      Id: this.substationTemplateId,
      utilityId: this.utilityId
    }

    this.ssTemplateService.getSchematicLayoutJsonById(obj).subscribe({
      next: (response: any) => {
        if (response?.body?.status == 200 || response?.body?.status == 'OK') {
          if (response?.body?.data !== "") 
          {
            this.dbHasPositionData = true;
            this.savedPositions = JSON.parse(response?.body?.data);
          } 
          else 
          {
            this.dbHasPositionData = false;
          }
          this.generateSchematicDiagram();
        } else {
          this.toastr.error(response?.body?.message);
        }
      }
    })
  }

  private createTooltips(){
    const tooltip = document.createElement('div');
    tooltip.style.position = 'absolute';
    tooltip.style.pointerEvents = 'none';
    tooltip.style.background = '#fff';
    tooltip.style.border = '1px solid #7a28ff';
    tooltip.style.padding = '4px 8px';
    tooltip.style.borderRadius = '4px';
    tooltip.style.fontSize = '12px';
    tooltip.style.display = 'none';
    tooltip.style.zIndex = '9999';
    tooltip.style.display = 'block';
    tooltip.style.visibility = 'hidden';

    this.cy!.container()!.appendChild(tooltip);

    let tooltipActive = false;

    this.cy!.on('mouseover', 'node', (e) => {
      tooltip.textContent = e.target.data('label');
      tooltipActive = true;
    });

    this.cy!.on('mousemove', (e) => {
      if (!tooltipActive) return;

      const zoom = parseFloat(getComputedStyle(document.documentElement).zoom || '1');

      const offsetX = e.originalEvent.offsetX / zoom;
      const offsetY = e.originalEvent.offsetY / zoom;

      const tooltipWidth = tooltip.offsetWidth;

      tooltip.style.left = `${offsetX - tooltipWidth - 7}px`;
      tooltip.style.top = `${offsetY - 30}px`;

      tooltip.style.visibility = 'visible';
    });

    this.cy!.on('mouseout', 'node', () => {
      tooltipActive = false;
      tooltip.style.visibility = 'hidden';
    });

    this.cy!.on('grab', 'node', () => {
      tooltipActive = false;
      tooltip.style.visibility = 'hidden';
    });

    this.cy!.on('tap', 'node', () => {
      tooltipActive = false;
      tooltip.style.visibility = 'hidden';
    });
  }

  ngOnDestroy() {
    if (this.cy) {
      this.cy.destroy();
      this.cy = null;
    }
  }
}